import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEditableDocument } from "../contexts/EditableDocumentContext";
import { useProposal } from "../contexts/ProposalContext";

// Components
import Layout from "../components/layout/Layout";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import CreateDocumentModal from "../components/documents/CreateDocumentModal";
import DuplicateDocumentModal from "../components/documents/DuplicateDocumentModal";
import RenameDocumentModal from "../components/documents/RenameDocumentModal";
import ManageAIInstructionsModal from "../components/documents/ManageAIInstructionsModal";
import DocumentList from "../components/documents/DocumentList";
import DocumentEditor from "../components/documents/DocumentEditor";
import DocumentBuilderAdvanced from "../components/documents/DocumentBuilderAdvanced";
import apiClient from "../services/apiClient";

const DocumentBuilder = () => {
  const { proposalId, documentId } = useParams();
  const navigate = useNavigate();

  const {
    documents,
    currentDocument,
    templates,
    loading,
    templatesLoading,
    error,
    fetchDocuments,
    fetchDocument,
    clearCurrentDocument,
    clearError,
    deleteDocument,
  } = useEditableDocument();

  const { currentProposal, fetchProposal } = useProposal();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [documentToDuplicate, setDocumentToDuplicate] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [documentToRename, setDocumentToRename] = useState(null);
  const [showManageInstructionsModal, setShowManageInstructionsModal] = useState(false);

  // Load proposal data
  useEffect(() => {
    if (proposalId) {
      fetchProposal(parseInt(proposalId));
      fetchDocuments(parseInt(proposalId));
    }
  }, [proposalId, fetchProposal, fetchDocuments]);

  // If documentId is provided, use the advanced editor
  if (documentId) {
    return <DocumentBuilderAdvanced />;
  }

  const handleCreateDocument = () => {
    setShowCreateModal(true);
  };

  const handleHolisticGenerate = () => {
    // Navigate to the holistic generation page
    navigate(`/proposals/${proposalId}/generate`);
  };

  const handleDocumentCreated = (newDocument) => {
    setShowCreateModal(false);
    // Navigate to the new document editor
    navigate(`/workspace/${proposalId}/document/${newDocument.document_id}`);
  };

  const handleDocumentSelect = (document) => {
    navigate(`/workspace/${proposalId}/document/${document.document_id}`);
  };

  const handleBackToDocuments = () => {
    navigate(`/proposal-development`);
  };

  const handleDeleteClick = (e, document) => {
    e.stopPropagation(); // Prevent card click
    setDocumentToDelete(document);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteDocument(documentToDelete.document_id);
      setShowDeleteModal(false);
      setDocumentToDelete(null);
      // Refresh the documents list
      fetchDocuments(parseInt(proposalId));
    } catch (err) {
      console.error("Failed to delete document:", err);
    }
  };

  const handleDuplicateClick = (e, document) => {
    e.stopPropagation(); // Prevent card click
    setDocumentToDuplicate(document);
    setShowDuplicateModal(true);
  };

  const handleDuplicateConfirm = async () => {
    if (!documentToDuplicate) return;

    try {
      const result = await apiClient.duplicateDocument(documentToDuplicate.document_id);

      // Refresh the documents list to show the new duplicate
      await fetchDocuments(parseInt(proposalId));

      // Close modal and reset state
      setShowDuplicateModal(false);
      setDocumentToDuplicate(null);

      // Show success message
      console.log('Document duplicated successfully:', result.data.document);
    } catch (error) {
      console.error('Failed to duplicate document:', error);
    }
  };

  const handleDuplicateCancel = () => {
    setShowDuplicateModal(false);
    setDocumentToDuplicate(null);
  };

  const handleRenameClick = (e, document) => {
    e.stopPropagation(); // Prevent card click
    setDocumentToRename(document);
    setShowRenameModal(true);
  };

  const handleRenameConfirm = async (newName) => {
    if (!documentToRename) return;

    try {
      await apiClient.renameDocument(documentToRename.document_id, newName);

      // Refresh the documents list to show the new name
      await fetchDocuments(parseInt(proposalId));

      // Close modal and reset state
      setShowRenameModal(false);
      setDocumentToRename(null);

      console.log('Document renamed successfully to:', newName);
    } catch (error) {
      console.error('Failed to rename document:', error);
      throw error; // Re-throw so modal can display error
    }
  };

  const handleRenameCancel = () => {
    setShowRenameModal(false);
    setDocumentToRename(null);
  };

  if (!proposalId) {
    return (
      <Layout title="Document Builder">
        <div className="text-center py-12">
          <FontAwesomeIcon
            icon="file-alt"
            className="text-4xl text-slate-300 mb-4"
          />
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            No Proposal Selected
          </h3>
          <p className="text-slate-600 mb-6">
            Please select a proposal to start building documents.
          </p>
          <button
            onClick={() => navigate("/proposals")}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Browse Proposals
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title={
        currentDocument
          ? `${currentDocument.document_name} - Document Builder`
          : `Document Builder - ${
              currentProposal?.proposal_name || "Loading..."
            }`
      }
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center">
                <FontAwesomeIcon
                  icon="edit"
                  className="mr-3 text-primary-500"
                />
                Document Builder
              </h1>
              {currentProposal && (
                <p className="text-slate-600 mt-2">
                  Building documents for{" "}
                  <span className="font-medium">
                    {currentProposal.proposal_name}
                  </span>
                  {currentProposal.customer_name && (
                    <span className="text-slate-500">
                      {" "}
                      • {currentProposal.customer_name}
                    </span>
                  )}
                </p>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={handleBackToDocuments}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors flex items-center space-x-2"
              >
                <FontAwesomeIcon icon="arrow-left" />
                <span>Back to Proposal Development</span>
              </button>

              <button
                onClick={() => setShowManageInstructionsModal(true)}
                className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                <FontAwesomeIcon icon="cog" />
                <span>Manage AI Instructions</span>
              </button>

              <button
                onClick={handleHolisticGenerate}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center space-x-2"
                disabled={templatesLoading}
              >
                <FontAwesomeIcon icon="magic" />
                <span>Create Proposal with AI</span>
              </button>
              {/* OLD: Section-based document creation - commented out */}
              {/* <button
                onClick={handleCreateDocument}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors flex items-center space-x-2"
                disabled={templatesLoading}
              >
                <FontAwesomeIcon icon="plus" />
                <span>Create Section-Based Document</span>
              </button> */}
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && <ErrorMessage message={error} onRetry={clearError} />}

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 min-h-[600px]">
          {/* Content Area */}
          <div className="p-6">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="lg" text="Loading..." />
              </div>
            )}

            {!loading && (
              <DocumentLibraryView
                documents={documents}
                onDocumentSelect={handleDocumentSelect}
                onCreateDocument={handleCreateDocument}
                onDeleteClick={handleDeleteClick}
                onDuplicateClick={handleDuplicateClick}
                onRenameClick={handleRenameClick}
              />
            )}
          </div>
        </div>
      </div>

      {/* Create Document Modal */}
      <CreateDocumentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onDocumentCreated={handleDocumentCreated}
        proposalId={parseInt(proposalId)}
        templates={templates}
      />

      {/* Duplicate Document Modal */}
      <DuplicateDocumentModal
        isOpen={showDuplicateModal}
        onClose={handleDuplicateCancel}
        document={documentToDuplicate}
        onConfirm={handleDuplicateConfirm}
      />

      {/* Rename Document Modal */}
      <RenameDocumentModal
        isOpen={showRenameModal}
        onClose={handleRenameCancel}
        document={documentToRename}
        onConfirm={handleRenameConfirm}
      />

      {/* Manage AI Instructions Modal */}
      <ManageAIInstructionsModal
        isOpen={showManageInstructionsModal}
        onClose={() => setShowManageInstructionsModal(false)}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                  <FontAwesomeIcon
                    icon="exclamation-triangle"
                    className="text-red-600 text-xl"
                  />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Delete Document
                </h3>
              </div>
              <p className="text-slate-600 mb-6">
                Are you sure you want to delete "
                <span className="font-medium">
                  {documentToDelete?.document_name}
                </span>
                "? This action cannot be undone and will delete all sections and
                content.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDocumentToDelete(null);
                  }}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Document
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

// Document Library View Component
const DocumentLibraryView = ({
  documents,
  onDocumentSelect,
  onCreateDocument,
  onDeleteClick,
  onDuplicateClick,
  onRenameClick,
}) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      Draft: { bg: "bg-slate-100", text: "text-slate-700", icon: "edit" },
      In_Progress: { bg: "bg-blue-100", text: "text-blue-700", icon: "clock" },
      Review: { bg: "bg-amber-100", text: "text-amber-700", icon: "eye" },
      Final: { bg: "bg-emerald-100", text: "text-emerald-700", icon: "check" },
      Archived: { bg: "bg-slate-100", text: "text-slate-500", icon: "archive" },
    };

    const config = statusConfig[status] || statusConfig["Draft"];

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        <FontAwesomeIcon icon={config.icon} className="mr-1" />
        {status}
      </span>
    );
  };

  const getDocumentTypeIcon = (documentType) => {
    const iconMap = {
      Technical_Proposal: "cogs",
      Past_Performance: "history",
      Key_Personnel: "users",
      Pricing_Proposal: "dollar-sign",
      Executive_Summary: "star",
      Corporate_Overview: "building",
      Custom: "file-alt",
    };

    return iconMap[documentType] || "file-alt";
  };

  if (documents.length === 0) {
    return (
      <div className="text-center py-12">
        <FontAwesomeIcon
          icon="file-alt"
          className="text-4xl text-slate-300 mb-4"
        />
        <h3 className="text-lg font-medium text-slate-900 mb-2">
          No Documents Yet
        </h3>
        <p className="text-slate-600 mb-6">
          Click "Create Proposal with AI" above to get started with AI-powered
          authoring.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-slate-900">
          Proposal Documents ({documents.length})
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {documents.map((document) => (
          <div
            key={document.document_id}
            className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer group relative"
            onClick={() => onDocumentSelect(document)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon
                  icon={getDocumentTypeIcon(document.document_type)}
                  className="text-primary-500"
                />
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  {document.document_type.replace("_", " ")}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                {getStatusBadge(document.status)}
                <button
                  onClick={(e) => onRenameClick(e, document)}
                  className="p-1.5 text-slate-600 hover:bg-slate-100 rounded transition-colors"
                  title="Rename document"
                >
                  <FontAwesomeIcon icon="edit" className="text-sm" />
                </button>
                <button
                  onClick={(e) => onDuplicateClick(e, document)}
                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Duplicate document"
                >
                  <FontAwesomeIcon icon="copy" className="text-sm" />
                </button>
                <button
                  onClick={(e) => onDeleteClick(e, document)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Delete document"
                >
                  <FontAwesomeIcon icon="trash" className="text-sm" />
                </button>
              </div>
            </div>

            <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-primary-600 transition-colors">
              {document.document_name}
            </h3>

            <div className="text-sm text-slate-600 space-y-1">
              <div className="flex items-center justify-between">
                <span>Sections:</span>
                <span className="font-medium">
                  {document.section_count || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Words:</span>
                <span className="font-medium">
                  {document.total_word_count || 0}
                </span>
              </div>
              {document.is_ai_generated && (
                <div className="flex items-center space-x-1 text-blue-600">
                  <FontAwesomeIcon icon="robot" className="text-xs" />
                  <span className="text-xs">AI Generated</span>
                </div>
              )}
            </div>

            <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
              Updated {new Date(document.updated_at).toLocaleDateString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentBuilder;
